import React, { useState, useEffect, useRef } from 'react';
import { FieldCard } from './FieldCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { PostLoginWrapper } from './PostLoginWrapper';
import { FIELDS_CACHE_KEY, clearAuthSession } from '../authSession';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

if (typeof window !== 'undefined') {
    const navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry && navEntry.type === 'reload') {
        sessionStorage.removeItem(FIELDS_CACHE_KEY);
    }
}

export const Fields = () => {
    const navigate = useNavigate();
    const [fields, setFields] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [trigger, setTrigger] = useState(false);
    const [visibleCount, setVisibleCount] = useState(999999);
    const [searchQuery, setSearchQuery] = useState('');

    // Estado para la imagen y para controlar la visibilidad del Dialog
    const [selectedImage, setSelectedImage] = useState(null);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(false);
    
    const CACHE_KEY = FIELDS_CACHE_KEY;
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutos en ms

    const getCachedFields = () => {
        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const { data, timestamp } = JSON.parse(raw);
            if (Date.now() - timestamp > CACHE_TTL) {
                sessionStorage.removeItem(CACHE_KEY);
                return null;
            }
            return data;
        } catch {
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        }
    };

    const setCachedFields = (data) => {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
        } catch {
            // sessionStorage lleno, ignorar
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            // Usar caché si no es un refresco forzado (trigger) y los datos son recientes
            if (!trigger) {
                const cached = getCachedFields();
                if (cached) {
                    setFields(cached);
                    setIsLoading(false);
                    return;
                }
            }

            setIsLoading(true);
            setError(null);

            try {
                const accessToken = localStorage.getItem('accessToken');
                if (!accessToken) {
                    handleLogout();
                    return;
                }

                const response = await fetch('/vitisad/get_campos', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                });

                const data = await response.json();

                switch (response.status) {
                    case 200:
                        setFields(data.data);
                        setCachedFields(data.data);
                        break;

                    case 401:
                        const newAccessToken = await refreshAccessToken();
                        if (newAccessToken) {
                            localStorage.setItem('accessToken', newAccessToken);
                            setTrigger(prev => !prev);
                        } else {
                            handleLogout();
                        }
                        break;

                    case 403:
                        handleLogout();
                        break;

                    default:
                        setError(data.error || 'Error desconocido');
                }

            } catch (error) {
                setError('Error de conexión: ' + error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [trigger]);

    const refreshAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const response = await fetch('/vitisad/refresh_token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refreshToken })
            });
            
            if (response.ok) {
                const data = await response.json();
                return data.accessToken;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error al renovar el token de acceso:", error);
            return null;
        }
    };

    const handleLogout = () => {
        clearAuthSession();
        navigate('/login');
    };

    const loadMoreFields = () => {
        setVisibleCount(prevCount => prevCount + 9);
    };

    const handleCreateField = () => {
        setError('');
        navigate('/createField');
    };

    const handleCardClick = async (field) => {
        navigate(`/field/${field.id}`, { state: { field } });
    };

    // Función para cerrar el Dialog
    const hideDialog = () => {
        setIsDialogVisible(false);
        setSelectedImage(null);
    };

    const filteredFields = fields.filter(field =>
        field.nombre?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <PostLoginWrapper error={error} isMain={true}>
            {isLoading ? (
                <div className="loading"></div>
            ) : (
                <>
                    {isImageLoading && (
                        <div className="loading-2"></div>
                    )}
                    <div className='w-11 flex flex-column align-items-center mb-8'>
                        <div className="p-input-icon-left w-full mt-3 mb-2" style={{ maxWidth: '500px' }}>
                            <i className="pi pi-search" style={{ color: 'var(--primary-color)', paddingLeft: '15px' }} />
                            <InputText
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Buscar parcela..."
                                className="w-full"
                                style={{ paddingLeft: '2.5rem', borderRadius: '20px' }}
                            />
                        </div>
                        <div 
                            className='w-full flex mobile-field-list desktop-field-list' 
                            style={{ minHeight: '75vh' }}
                        >
                            {filteredFields.slice(0, visibleCount).map((field) => {
                                const prescription = (
                                    field.id_kind_of_application >= 1 && field.id_kind_of_application <= 4
                                ) ? field.prescription : null;

                                const applied = (
                                    field.id_kind_of_application >= 1 && field.id_kind_of_application <= 4
                                ) ? field.applied : null;

                                return (
                                    <FieldCard 
                                        key={field.id}
                                        field_predicciones={field.datos_clima?.predicciones ?? []}
                                        field_date={field.ndvi?.length > 0 ? field.ndvi[field.ndvi.length - 1].fecha : null}
                                        crop_name={field.cultivo}
                                        plot_name={field.nombre} 
                                        estado_vegetativo_valor={field.estado_vegetativo ? Math.round(field.estado_vegetativo * 100) : 0}
                                        estado_vegetativo_color={field.ndvi?.length > 0 ? field.ndvi[field.ndvi.length - 1].estado_vegetativo_color : null}
                                        worker_name={field.email_usuario}
                                        onClick={() => handleCardClick(field)}
                                        coordenadas={field.coordenadas}
                                        cargando={!field.datos_clima || !field.ndvi}
                                    />
                                );
                            })}
                            {/* Relleno para maquetación */}
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                            <div className='w-11' style={{ visibility: 'hidden', width: '450px', height: '0px', maxWidth: '450px' }} />
                        </div>

                        <div
                    className="fixed bottom-0 left-0 w-full bg-white p-3 shadow-2 flex align-items-center justify-content-center cursor-pointer"
                    style={{ zIndex: 1 }}
                    onClick={handleCreateField}
                >
                    <div className="flex align-items-center gap-2 no-tap-highlight">
                        <FontAwesomeIcon className='svg-settings-add' icon={faPlus} style={{ color: 'var(--primary-color)' }} />
                        <span className="font-medium" style={{ color: 'var(--primary-color)' }}>Añadir campo</span>
                    </div>
                </div>
                    </div>
                </>
            )}

            {/* Dialog de PrimeReact para mostrar la imagen */}
            <Dialog
                id='image-dialog'
                header={
                    <div>
                        <div>Trabajo realizado</div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px', marginLeft: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center'}}>
                                <div style={{ width: '13px', height: '13px', backgroundColor: 'red', borderRadius: '50%', marginRight: '5px' }}></div>
                                <span style={{ fontSize: '14px' }}>Por debajo de la dosis correcta</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                <div style={{ width: '13px', height: '13px', backgroundColor: 'green', borderRadius: '50%', marginRight: '5px' }}></div>
                                <span style={{ fontSize: '14px' }}>Dosis correcta</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px'  }}>
                                <div style={{ width: '13px', height: '13px', backgroundColor: 'blue', borderRadius: '50%', marginRight: '5px' }}></div>
                                <span style={{ fontSize: '14px' }}>Por encima de la dosis correcta</span>
                            </div>
                        </div>
                    </div>
                }
                className='w-11'
                visible={isDialogVisible}
                style={{ maxWidth: '559px' }}
                modal
                onHide={hideDialog}
                focusOnShow={false}
            >
                <img 
                    src={selectedImage} 
                    alt="Imagen PNOA" 
                    style={{ 
                        display: 'block', 
                        margin: '0 auto', 
                        maxWidth: '100%', 
                        maxHeight: '70vh' 
                    }}
                />
            </Dialog>
        </PostLoginWrapper>
    );
};
